import {
  MENU_VIEW_KEYS,
  PERMISSIONS,
  PERMISSION_LABEL,
  ROLES,
  ROLE_LABEL,
  VIEW_LABEL,
  isMenuViewKey,
  type MenuViewKey,
  type Permission,
  type Role,
  type ViewKey,
} from './role';

/**
 * The access matrix is runtime editable by Management, section 4.2. Nothing in
 * the application may assume the seeded values; `session.permissions` and
 * `session.views` are resolved by the server for the signed in user and the
 * frontend reads those, not a role table it keeps locally.
 *
 * These functions exist so the mock server, the admin screen and the tests can
 * all reason about a matrix without three copies of the rule.
 */

export interface AccessMatrix {
  roles: { key: Role; label: string }[];
  permissions: { key: Permission; label: string; roles: Role[] }[];
  views: { key: MenuViewKey; label: string; roles: Role[]; locked?: boolean }[];
  rowVersion: string;
}

/** The grants the client approved. Section 4.2. */
const SEED_PERMISSION_ROLES: Record<Permission, Role[]> = {
  editDates: ['management'],
  markUrgent: ['management', 'reception'],
  intake: ['management', 'reception', 'cadcam'],
  assign: ['management', 'reception'],
  pay: ['management', 'accountant'],
  ship: ['management', 'reception'],
  seeIdentity: ['management', 'reception', 'accountant', 'cadcam'],
  finalcheck: ['management'],
  admin: ['management'],
  // Editing a case after intake. Reception is not seeded; the matrix is
  // editable at runtime if the lab wants it. System test handover 2.4.
  editCase: ['management'],
  // Items and prices. Section 2.5 of the system test handover.
  pricing: ['management', 'accountant'],
  // Sending a dispatched case back to the floor as a remake.
  redo: ['management'],
  /*
   * Deleting a case. Whoever holds approveDeletion deletes outright; anybody
   * holding only deleteCase asks, and the case goes when somebody else agrees.
   * Management holds both, which makes "reception's deletion goes to the manager
   * for approval" true on day one without the code naming a role.
   */
  deleteCase: ['management', 'reception'],
  approveDeletion: ['management'],
  /*
   * Photographs of finished work taken by somebody else. Your own you can
   * always see, with no permission behind it. Section 2.2 of the request of
   * 13 September 2026.
   */
  seeWorkProof: ['management'],
  /*
   * Taking a case off another bench. Section 7 of the request of 20 September
   * 2026, and the one permission in that request worth explaining to the client:
   * it lets any bench pull any case, which is what they asked for and also what a
   * lab might want to switch off after the first Tuesday it goes wrong.
   *
   * The three roles that work a bench. Reception and Accounts work none, so they
   * have nowhere to take a case TO; Management works every bench and needs no
   * transfer. Seeded rather than hard coded, so the lab can take it back from one
   * bench without a deploy.
   */
  takeOver: ['management', 'tech', 'cadcam'],
  /*
   * A case's own history and log. Section 6 of the request of 20 September 2026.
   *
   * Everybody who could open a case could read its timeline; the client decided
   * otherwise. Management only, and movable like every other row. A technician
   * whose case came back still sees WHY, because the rework banner reads the
   * notes read model and is gated on nothing but opening the case, and still sees
   * who has the case now and how long it has been there, which are on the kit.
   */
  seeCaseLog: ['management'],
  /*
   * Archiving a case and taking it out again. Section 4 of the request of
   * 24 September 2026. The two roles that may delete one, since archiving is
   * the gentler of the two: nothing is purged and the case comes back whole.
   */
  archiveCase: ['management', 'reception'],
  /*
   * Stopping a clock somebody else has running, from the board. Request of
   * 25 September 2026: the clock runs until Pause or Done, so a forgotten one
   * runs all night, and the manager who sees it on the board could do nothing
   * about it but find the technician. Management, who read the board.
   */
  stopWork: ['management'],
  /*
   * Saving a photograph out of the image viewer. The request of 5 October 2026
   * (third), section 6: a download is offered only to roles allowed to
   * download. Management on day one, and movable like every other row. It
   * decides the button and the logged route and nothing more: whoever may see
   * a photograph has it on their screen.
   */
  downloadPhotos: ['management'],
  /*
   * Adding, editing and deleting a doctor's or a patient's record. The request
   * of 6 October 2026 (second), items 4 and 5. The two desks that take cases in
   * and so know the clinics. Reading the records is not this: that follows
   * seeIdentity, as the two names on a case do, and booking a case in under a
   * name nobody has typed before needs only what intake needs.
   *
   * Since the request of 6 October 2026 (third), item 4, it is no longer the
   * name: adding a record, editing a doctor's details and deleting a record
   * with no cases are this, and renaming is the row below.
   */
  manageClients: ['management', 'reception'],
  /*
   * Changing a doctor's or a patient's name. The request of 6 October 2026
   * (third), item 4: renaming should be controllable in access. A name is on
   * every case of the record, the delivered and the archived among them, so a
   * rename rewrites more than the desk that made it can see. Management on day
   * one, and granted to a role or to one person like every other row.
   */
  renameClients: ['management'],
  /*
   * Merging one doctor into another. The same request, item 1. Every case of
   * the duplicate moves to the doctor kept, the duplicate's record is removed,
   * and none of it can be undone. Management.
   */
  mergeDoctors: ['management'],
};

/**
 * `perf` is granted to nobody, and that is the whole of the client's rule.
 *
 * `perf` is My performance: one person's own figures. `staff` is Staff
 * productivity: everybody's, and it belongs to Management. Technicians never
 * held `perf`, which is item 6 of section 17, and the client's third review
 * extended that to CAD / CAM in these words: only management can see the
 * performance of employees.
 *
 * Granting `perf` to Management instead of to nobody would have been the
 * smaller edit and the wrong one. A manager does not work a bench, so their
 * own figures are empty, and they already read everybody's through `staff`.
 * An empty row list is a deliberate statement, not an oversight: the screen
 * and its endpoint remain, and the matrix is editable at runtime, so the lab
 * can hand somebody their own numbers without a deploy if it changes its mind.
 */
const SEED_VIEW_ROLES: Record<MenuViewKey, Role[]> = {
  overview: ['management'],
  /*
   * A person's own page of widgets, and where whoever holds it lands. The
   * lab's request of 12 October 2026. Reception's and Accounts' to begin with:
   * Management's Overview is the same kind of screen and stays its home, so it
   * is not given this one. It asks the server for nothing of its own. Each
   * widget reads what one of the person's other screens already opens, and is
   * offered only to somebody who holds that screen: see `dashboard/widgets`.
   */
  dashboard: ['reception', 'accountant'],
  line: ['management'],
  orders: ['management', 'reception'],
  finalcheck: ['management'],
  reports: ['management'],
  archive: ['management', 'reception', 'accountant'],
  activity: ['management'],
  staff: ['management'],
  users: ['management'],
  // The routing mode and the geofence. Section 2.3 of the system test handover.
  settings: ['management'],
  // The price list. Section 2.5 of the system test handover.
  items: ['management', 'accountant'],
  intake: ['management', 'reception', 'cadcam'],
  shipping: ['reception'],
  payments: ['accountant'],
  /*
   * A case the lab has stopped, usually over money. Reception holds a case and
   * takes it off again, and management sees the whole floor. Accounts held this
   * screen until the request of 5 October 2026 (third), section 10, left them
   * Payments, Items and Archive: they still hold and release a case, on its
   * row on Payments. It is no longer theirs by default, and can be given back:
   * the request of 6 October 2026 (fourth).
   */
  onhold: ['management', 'reception'],
  /*
   * Doctors and patients, each with a profile and its cases. The request of
   * 6 October 2026 (second), item 4. Management and reception, the roles that
   * add and edit the records.
   */
  doctors: ['management', 'reception'],
  patients: ['management', 'reception'],
  queue: ['cadcam', 'tech'],
  perf: [],
};

/**
 * `users` is locked to Management and the server must reject any request that
 * removes it. Otherwise an administrator can lock every user out of the system
 * with one checkbox and there is no way back in without a database edit.
 */
export const LOCKED_VIEW_GRANTS: ReadonlyArray<{ view: MenuViewKey; role: Role }> = [
  { view: 'users', role: 'management' },
];

export function isLockedView(view: MenuViewKey): boolean {
  return LOCKED_VIEW_GRANTS.some((grant) => grant.view === view);
}

/**
 * `admin` is locked to Management for the same reason: the access matrix can
 * only be edited by a holder of `admin`, so taking it away from Management
 * locks everybody out of this screen as surely as taking the screen away.
 */
export const LOCKED_PERMISSION_GRANTS: ReadonlyArray<{ permission: Permission; role: Role }> = [
  { permission: 'admin', role: 'management' },
];

export function isLockedPermission(permission: Permission): boolean {
  return LOCKED_PERMISSION_GRANTS.some((grant) => grant.permission === permission);
}

export function seedAccessMatrix(rowVersion = 'seed'): AccessMatrix {
  return {
    roles: ROLES.map((key) => ({ key, label: ROLE_LABEL[key] })),
    permissions: PERMISSIONS.map((key) => ({
      key,
      label: PERMISSION_LABEL[key],
      roles: [...SEED_PERMISSION_ROLES[key]],
    })),
    views: MENU_VIEW_KEYS.map((key) => ({
      key,
      label: VIEW_LABEL[key],
      roles: [...SEED_VIEW_ROLES[key]],
      ...(isLockedView(key) ? { locked: true } : {}),
    })),
    rowVersion,
  };
}

/** A frozen copy for defaults and tests. Callers that mutate must use `seedAccessMatrix`. */
export const SEED_ACCESS_MATRIX: AccessMatrix = seedAccessMatrix();

/**
 * The four sub-views have no grant of their own. Each is reached from a view
 * that does, and holding any one of its parents is enough. `scan` resolves to
 * Management, CAD / CAM and Technician, which is the role list in section 5.
 */
const SUB_VIEW_PARENTS: Record<Exclude<ViewKey, MenuViewKey>, MenuViewKey[]> = {
  stage: ['line', 'finalcheck'],
  /*
   * `line` and `finalcheck` alongside `queue`.
   *
   * A case opens from three places: a technician's queue, a station on the
   * production line, and Final Check. Listing only `queue` meant a manager
   * tapping a case on the live floor was redirected to their home screen,
   * because Management holds `line` and does not hold `queue`. The case detail
   * is the same screen wherever it is reached from, and reaching it from a
   * screen you already hold is not a second grant.
   */
  job: ['queue', 'line', 'finalcheck'],
  record: ['archive'],
  /*
   * `finalcheck` alongside `queue` and `overview`, since the lab's request of
   * 11 October 2026. A case at Final Check is taken by scanning its tray, as at
   * any department, and the job view sends its reader to the scanner to do it.
   * A receptionist given Final Check holds neither a queue nor the Overview, so
   * that button opened a screen they were then turned away from. As the lab's
   * matrix starts, Final Check is Management's, who hold the Overview, and a
   * supervisor's, who holds a queue: nobody's menu changes until somebody else
   * is given the screen.
   */
  scan: ['queue', 'overview', 'finalcheck'],
};

export function can(
  role: Role,
  permission: Permission,
  matrix: AccessMatrix = SEED_ACCESS_MATRIX,
): boolean {
  const entry = matrix.permissions.find((p) => p.key === permission);
  if (!entry) return false;
  return entry.roles.includes(role);
}

export function allowedView(
  role: Role,
  view: ViewKey,
  matrix: AccessMatrix = SEED_ACCESS_MATRIX,
): boolean {
  if (!isMenuViewKey(view)) {
    return SUB_VIEW_PARENTS[view].some((parent) => allowedView(role, parent, matrix));
  }
  const entry = matrix.views.find((v) => v.key === view);
  if (!entry) return false;
  return entry.roles.includes(role);
}

/**
 * Whether a route may be opened, given the view list the SERVER resolved for
 * this session. Section 5.1: the menu, the mobile tabs and the route guard all
 * read from `session.views`.
 *
 * This is not recomputing permissions, which section 7.2 forbids. It is mapping
 * a sub-screen to the screen it is reached from: `/queue/K-1084` is part of the
 * queue, and a server that lists `queue` has already granted it. The four
 * sub-views may or may not appear in `session.views` depending on how the API
 * resolves them, and this works either way.
 */
export function canOpenView(views: readonly ViewKey[], view: ViewKey): boolean {
  if (views.includes(view)) return true;
  if (isMenuViewKey(view)) return false;
  return SUB_VIEW_PARENTS[view].some((parent) => views.includes(parent));
}

/**
 * The screens a case is opened from, and so the screens that let a person read a
 * case's history. The job view is reached from the queue, the production line
 * and Final Check; the case record from Orders and the archive; the overview's
 * attention list opens a case too; shipping and payments act on a case by its
 * row. Access to a case, not the activity view, which is Management's alone and
 * shows the whole lab. Section 4.3 of the request of 13 September 2026.
 */
export const CASE_VIEWS: readonly MenuViewKey[] = [
  'queue',
  'line',
  'finalcheck',
  'orders',
  'archive',
  'overview',
  'shipping',
  'payments',
];

/** Whether a caller with these views may open a case, and so read its history. */
export function opensCases(views: readonly ViewKey[]): boolean {
  return CASE_VIEWS.some((view) => views.includes(view));
}

export function permissionsFor(role: Role, matrix: AccessMatrix = SEED_ACCESS_MATRIX): Permission[] {
  return matrix.permissions.filter((p) => p.roles.includes(role)).map((p) => p.key);
}

/**
 * Menu order, from the prototype's NAVORDER. Any view granted to a role but not
 * named in its preferred order is appended, so granting a screen in the admin
 * screen puts it in that role's menu on the next render with no code change.
 * Section 5.1.
 */
export const MENU_ORDER: Record<Role, MenuViewKey[]> = {
  management: [
    'overview',
    // After the Overview, for a manager who is given one.
    'dashboard',
    'line',
    'orders',
    'finalcheck',
    'onhold',
    'reports',
    'archive',
    'activity',
    'staff',
    'users',
    'settings',
    'items',
    // Last for both roles that hold them, so the screens a phone's tab bar
    // shows, the first of each list, are the ones it showed before. The
    // request of 6 October 2026 (second), item 4.
    'doctors',
    'patients',
  ],
  // The Dashboard first for every role that holds one: it is where they land.
  reception: ['dashboard', 'orders', 'intake', 'shipping', 'onhold', 'archive', 'doctors', 'patients'],
  // What Accounts is given to begin with, in the order the request of
  // 5 October 2026 (third), section 10, lists it. A default and not a limit,
  // since the request of 6 October 2026 (fourth): any other screen an
  // accountant is granted comes after these three. See accountsScreens.
  accountant: ['dashboard', 'payments', 'items', 'archive'],
  cadcam: ['dashboard', 'queue', 'intake'],
  tech: ['dashboard', 'queue'],
};

export function viewsFor(role: Role, matrix: AccessMatrix = SEED_ACCESS_MATRIX): MenuViewKey[] {
  return inMenuOrder(
    role,
    matrix.views.filter((v) => v.roles.includes(role)).map((v) => v.key),
  );
}

/**
 * The role's preferred order first, then anything else granted, in the matrix's
 * order.
 *
 * Every list of screens a role or a person ends up with passes through here,
 * and it only ever reorders: no screen is added and none is taken away. It
 * used to be where Accounts was cut down to three screens; the request of
 * 6 October 2026 (fourth) ended that, and an accountant's list is ordered as
 * every other role's is, its own three first.
 */
function inMenuOrder(role: Role, allowed: readonly MenuViewKey[]): MenuViewKey[] {
  const preferred = MENU_ORDER[role].filter((view) => allowed.includes(view));
  const rest = allowed.filter((view) => !preferred.includes(view));
  return [...preferred, ...rest];
}

/**
 * The exceptions one person has, on top of their role. Request of 13 September
 * 2026, section 2. Grant and deny are two lists on one record; a key in neither
 * is inherited from the role.
 */
export interface UserAccessOverride {
  grantedPermissions: readonly Permission[];
  grantedViews: readonly ViewKey[];
  deniedPermissions: readonly Permission[];
  deniedViews: readonly ViewKey[];
}

/**
 * What a department supervisor is given on top of their role. Section 11 of the
 * request of 20 September 2026.
 *
 * Passing Final Check and assigning work (request of 4 October 2026, section
 * 5), and the screen to do the first on. Resolved into the session
 * rather than written into the matrix, so the lab still reads
 * "Final Check: Management" in the matrix and sees the exception on the person:
 * a supervisor is a ceramist who also does something more, not a fifth role.
 *
 * `isSupervisor` and `isIntakeOperator` are the two flags of this kind. The
 * intake one is read by `worksThisBench` rather than here, because it opens a
 * BENCH rather than granting a permission.
 */
export const SUPERVISOR_PERMISSIONS: readonly Permission[] = ['finalcheck', 'assign'];
export const SUPERVISOR_VIEWS: readonly MenuViewKey[] = ['finalcheck'];

/**
 * Deny wins. The role is the rule; the override and the supervisor flag are the
 * exceptions. What the role grants, plus the supervisor's, plus what this person
 * is granted, minus what this person is denied, with the role's permissions in
 * the matrix's order and the extra ones after.
 *
 * A DENY still wins over the supervisor flag. Somebody the lab has explicitly
 * taken Final Check away from does not get it back by being made a supervisor of
 * their department: the per-person deny is the most specific statement there is,
 * and this is the one place that has to decide between them.
 */
export function effectivePermissions(
  role: Role,
  matrix: AccessMatrix,
  override: UserAccessOverride | null,
  options: { isSupervisor?: boolean } = {},
): Permission[] {
  const fromRole = permissionsFor(role, matrix);
  const fromFlag = options.isSupervisor === true ? SUPERVISOR_PERMISSIONS : [];
  const base = [...fromRole, ...fromFlag.filter((permission) => !fromRole.includes(permission))];
  if (override === null) return base;

  const granted = PERMISSIONS.filter(
    (permission) => override.grantedPermissions.includes(permission) && !base.includes(permission),
  );
  return [...base, ...granted].filter(
    (permission) => !override.deniedPermissions.includes(permission),
  );
}

/**
 * Deny wins, as for permissions, and the menu keeps the ordering viewsFor applies:
 * the role's preferred order first and anything else appended, so a screen
 * delegated to one person lands in a sensible place rather than at a random index.
 * Only a menu view can be granted; the sub-views follow the screens they open from.
 */
export function effectiveViews(
  role: Role,
  matrix: AccessMatrix,
  override: UserAccessOverride | null,
  options: { isSupervisor?: boolean } = {},
): MenuViewKey[] {
  const fromFlag = options.isSupervisor === true ? SUPERVISOR_VIEWS : [];
  if (override === null) {
    const granted = viewsFor(role, matrix);
    const extra = fromFlag.filter((view) => !granted.includes(view));
    return extra.length === 0 ? granted : inMenuOrder(role, [...granted, ...extra]);
  }

  const allowed = matrix.views
    .filter(
      (view) =>
        (view.roles.includes(role) ||
          override.grantedViews.includes(view.key) ||
          fromFlag.includes(view.key)) &&
        !override.deniedViews.includes(view.key),
    )
    .map((view) => view.key);
  return inMenuOrder(role, allowed);
}

const HOME_VIEW: Record<Role, MenuViewKey> = {
  management: 'overview',
  reception: 'orders',
  accountant: 'payments',
  cadcam: 'queue',
  tech: 'queue',
};

/**
 * Where a role lands after signing in, and where the route guard redirects to
 * when someone opens a screen they no longer hold: the Dashboard where it is
 * held, and otherwise the role's own screen below. Falls back to the first
 * granted view, because the matrix is editable and a role can lose its usual
 * home. Null means the role has no views at all, which the caller must treat
 * as a failed sign in rather than an empty shell.
 *
 * Read off the screens the person ends up with, whatever gave them: the role,
 * their own record, or the supervisor flag. So the home is always one of the
 * screens the session lists, and somebody left with Final Check alone lands
 * there rather than being told they have no screen.
 */
export function homeViewFor(
  role: Role,
  matrix: AccessMatrix = SEED_ACCESS_MATRIX,
  override: UserAccessOverride | null = null,
  options: { isSupervisor?: boolean } = {},
): ViewKey | null {
  return homeAmong(role, effectiveViews(role, matrix, override, options));
}

/**
 * The landing screen among the screens a person ends up with. The Dashboard
 * for whoever holds it, whatever their role: that is what one is for, and it
 * leaves the tick in the matrix the whole of the decision. Otherwise the
 * role's own, and where that is not held, the first that is. The lab's
 * request of 12 October 2026, and the API's HomeScreens.Home.
 *
 * Its own function because a build of the app that does not know the
 * Dashboard is sent its screens without it, and lands on what is left: see
 * `screensKnownTo`.
 */
export function homeAmong(role: Role, views: readonly ViewKey[]): ViewKey | null {
  if (views.includes('dashboard')) return 'dashboard';
  const preferred = HOME_VIEW[role];
  if (views.includes(preferred)) return preferred;
  return views[0] ?? null;
}

/**
 * Section 7.10 item 7. `PUT /access-matrix` returns 422 when this is non-empty.
 * The check lives here rather than in the handler so the admin screen can grey
 * the checkbox out with the same rule the server refuses on.
 */
export function lockedGrantViolations(matrix: AccessMatrix): string[] {
  const views = LOCKED_VIEW_GRANTS.flatMap(({ view, role }) => {
    const entry = matrix.views.find((v) => v.key === view);
    if (entry && entry.roles.includes(role)) return [];
    return [`${VIEW_LABEL[view]} cannot be removed from ${ROLE_LABEL[role]}.`];
  });
  const permissions = LOCKED_PERMISSION_GRANTS.flatMap(({ permission, role }) => {
    const entry = matrix.permissions.find((p) => p.key === permission);
    if (entry && entry.roles.includes(role)) return [];
    return [`${PERMISSION_LABEL[permission]} cannot be removed from ${ROLE_LABEL[role]}.`];
  });
  return [...views, ...permissions];
}
